import {
  RecommendationAction,
  RecommendationActionEntry,
  RecommendationActionsSnapshot,
} from '@/experiences/sales-portal/domain/pricing-agent/blocs/stores/recommendation-actions-store-types';
import type { PricingRecommendationRow } from '@/experiences/sales-portal/domain/pricing-agent/screens/types';

/** The only fields the action layer needs from a recommendation row. */
export type RecommendationRowRef = Pick<
  PricingRecommendationRow,
  | 'grossProfitInPercentage'
  | 'newPriceInMicros'
  | 'reviewStatus'
  | 'supplierUUID'
  | 'uuid'
>;

// A row the user hasn't touched keeps whatever the backend already has. For an
// overridden row the backend's applied price/GP become the override values.
const backendEntry = (row: RecommendationRowRef): RecommendationActionEntry => {
  if (row.reviewStatus === RecommendationAction.Rejected) {
    return {
      action: RecommendationAction.Rejected,
      recommendationUUID: row.uuid,
      supplierUUID: row.supplierUUID,
    };
  }
  if (row.reviewStatus === RecommendationAction.Overridden) {
    return {
      action: RecommendationAction.Overridden,
      overrideGrossProfitInPercentage: row.grossProfitInPercentage,
      overridePriceInMicros: row.newPriceInMicros,
      recommendationUUID: row.uuid,
      supplierUUID: row.supplierUUID,
    };
  }
  return {
    action: RecommendationAction.Accepted,
    recommendationUUID: row.uuid,
    supplierUUID: row.supplierUUID,
  };
};

/**
 * The effective status of every row on an auto-accepted screen: the user's
 * explicit edit where there is one, otherwise the row's backend status. These
 * rows have no untouched state — leaving one alone is a decision to keep its
 * current status — so this is both what Publish submits and what the
 * save-progress sync compares against.
 */
export const resolveAutoAcceptedActions = (
  rows: RecommendationRowRef[],
  actions: RecommendationActionsSnapshot,
): RecommendationActionsSnapshot =>
  Object.fromEntries(
    rows.map((row) => [row.uuid, actions[row.uuid] ?? backendEntry(row)]),
  );

/**
 * Drops rejections from a snapshot. Auto-accepted screens persist rejections as
 * they happen through the save-progress sync, and nothing goes live for a
 * rejected row, so the publish payload carries only the prices to apply.
 */
export const omitRejectedActions = (
  actions: RecommendationActionsSnapshot,
): RecommendationActionsSnapshot =>
  Object.fromEntries(
    Object.entries(actions).filter(
      ([, entry]) => entry.action !== RecommendationAction.Rejected,
    ),
  );
